import React, { useState, useEffect, useContext, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../App";
import { toast } from "sonner";
import {
  Scale, Plus, Pencil, Trash2, X, Save, Loader2,
  ArrowLeft, RefreshCw, Printer, Search, Receipt, ShoppingCart,
  Settings, Check,
} from "lucide-react";
import DateInput from "../components/ui/DateInput";
import { leerPeso } from "../lib/balanza";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BTN_PRI = "flex items-center gap-2 px-4 py-2 text-sm bg-arandu-blue hover:bg-blue-600 text-white rounded-lg transition-all font-body disabled:opacity-50";
const BTN_SEC = "flex items-center gap-2 px-3 py-2 text-sm bg-white/5 hover:bg-white/10 text-slate-300 rounded-lg transition-all border border-white/10";
const INPUT = "w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-arandu-blue/60";
const LABEL = "block text-xs text-slate-400 mb-1";

const DIRECCION_LABEL = { compra: "Compra", venta: "Venta" };
const DIRECCION_COLOR = {
  compra: "text-amber-400 bg-amber-400/10 border-amber-400/20",
  venta: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20",
};

const fmtKg = (n) => `${Math.round(Number(n) || 0).toLocaleString("es-PY")} kg`;

// ── Cálculo comercial (mismo algoritmo que el backend — solo para vista previa
// en vivo mientras se carga el formulario; el backend recalcula igual al guardar) ──
function redondeoComercial(v) {
  return Math.round(v + 0.0001);
}
function calcularPesoNeto(bruto, tara) {
  return redondeoComercial((Number(bruto) || 0) - (Number(tara) || 0));
}
function calcularPesoFinal(pesoNeto, porcentajes) {
  let totalKilosDescuento = 0;
  for (const pct of porcentajes) {
    const kilosLinea = pesoNeto * ((Number(pct) || 0) / 100);
    totalKilosDescuento += redondeoComercial(kilosLinea);
  }
  return Math.round(pesoNeto - totalKilosDescuento);
}

// ── Buscador simple de entidad (proveedor/cliente/chofer) — mismo patrón que Transporte ──
function EntidadSearchSelect({ entidades, value, onSelect, placeholder }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return (entidades || []).slice(0, 12);
    const q = query.toLowerCase();
    return (entidades || []).filter(e => (e.nombre || "").toLowerCase().includes(q) || (e.ruc || "").toLowerCase().includes(q)).slice(0, 12);
  }, [entidades, query]);

  const seleccionado = (entidades || []).find(e => e.id === value);

  if (seleccionado) {
    return (
      <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2">
        <span className="text-sm text-white flex-1 truncate">{seleccionado.nombre}</span>
        <button type="button" onClick={() => onSelect(null)} className="text-slate-500 hover:text-white">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          className={`${INPUT} pl-8`}
          placeholder={placeholder}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
        />
      </div>
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-arandu-dark border border-white/10 rounded-lg shadow-xl max-h-56 overflow-y-auto">
          {filtered.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-500">Sin resultados</div>
          )}
          {filtered.map(e => (
            <button
              type="button"
              key={e.id}
              onClick={() => { onSelect(e.id); setQuery(""); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-white"
            >
              {e.nombre}{e.ruc ? ` · ${e.ruc}` : ""}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BasculaPage() {
  const navigate = useNavigate();
  const { token, hasPermission, activeEmpresaPropia, user } = useContext(AuthContext);
  const headers = { Authorization: `Bearer ${token}` };
  const slug = activeEmpresaPropia?.slug || "";
  const logoQs = slug ? `?logo_tipo=${encodeURIComponent(slug)}` : "";
  const logoQsAnd = slug ? `&logo_tipo=${encodeURIComponent(slug)}` : "";

  const isAdmin = user?.role === "admin" || user?.role === "super_admin" || user?.role === "gerente";
  const canVer = isAdmin || hasPermission("bascula.ver");
  const canCrear = isAdmin || hasPermission("bascula.crear");
  const canEditar = isAdmin || hasPermission("bascula.editar");
  const canEliminar = isAdmin || hasPermission("bascula.eliminar");
  const canConfigurarDescuentos = isAdmin || hasPermission("bascula.configurar_descuentos");

  const [tab, setTab] = useState("pesajes");
  const [pesajes, setPesajes] = useState([]);
  const [tiposDescuento, setTiposDescuento] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [choferes, setChoferes] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [productos, setProductos] = useState([]);
  const [equipos, setEquipos] = useState([]);
  const [capturando, setCapturando] = useState(null); // "peso_bruto" | "tara" | null
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [modalPesaje, setModalPesaje] = useState(null); // null | {} | doc
  const [seleccion, setSeleccion] = useState(new Set()); // ids de pesajes de venta a facturar juntos
  const [pesajeAImprimir, setPesajeAImprimir] = useState(null);
  const [modalTipoDescuento, setModalTipoDescuento] = useState(null);
  const [saving, setSaving] = useState(false);

  const emptyPesaje = () => ({
    direccion: "compra",
    fecha: new Date().toISOString().slice(0, 10),
    producto_id: null,
    entidad_id: null,
    chofer_id: null,
    vehiculo_id: null,
    peso_bruto: "",
    tara: "",
    observaciones: "",
    descuentos: {}, // { tipo_descuento_id: porcentaje }
    equipo_id: null,
    origen_peso_bruto: "manual",
    origen_peso_tara: "manual",
  });
  const [pesajeForm, setPesajeForm] = useState(emptyPesaje());
  const [tipoDescuentoForm, setTipoDescuentoForm] = useState({ nombre: "", activo: true });

  const fetchAll = useCallback(async () => {
    if (!canVer) { setLoading(false); return; }
    try {
      fetch(`${API}/admin/equipos-bascula/disponibles${logoQs}`, { headers })
        .then(r => (r.ok ? r.json() : []))
        .then(setEquipos)
        .catch(() => setEquipos([]));
      const [rPes, rTipos, rProv, rCli, rCho, rVeh, rProd] = await Promise.all([
        fetch(`${API}/admin/bascula/pesajes${logoQs}`, { headers }),
        fetch(`${API}/admin/bascula/tipos-descuento${logoQs}`, { headers }),
        fetch(`${API}/admin/entidades?es_proveedor=true${logoQsAnd}`, { headers }),
        fetch(`${API}/admin/entidades?es_cliente=true${logoQsAnd}`, { headers }),
        fetch(`${API}/admin/entidades?es_chofer=true${logoQsAnd}`, { headers }),
        fetch(`${API}/admin/transporte/vehiculos${logoQs}`, { headers }),
        fetch(`${API}/admin/productos${logoQs}`, { headers }),
      ]);
      if (rPes.ok) setPesajes(await rPes.json());
      if (rTipos.ok) setTiposDescuento(await rTipos.json());
      if (rProv.ok) setProveedores(await rProv.json());
      if (rCli.ok) setClientes(await rCli.json());
      if (rCho.ok) setChoferes(await rCho.json());
      if (rVeh.ok) setVehiculos(await rVeh.json());
      if (rProd.ok) setProductos(await rProd.json());
    } catch { toast.error("Error al cargar Báscula"); }
    setLoading(false);
    setRefreshing(false);
  }, [token, slug]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleRefresh = () => { setRefreshing(true); fetchAll(); };

  // ── Pesajes ──────────────────────────────────────────────────────────────────
  const openPesaje = (p) => {
    if (p) {
      const descMap = {};
      (p.descuentos || []).forEach(d => { descMap[d.tipo_descuento_id] = d.porcentaje; });
      setPesajeForm({
        direccion: p.direccion, fecha: p.fecha, producto_id: p.producto_id,
        entidad_id: p.entidad_id, chofer_id: p.chofer_id, vehiculo_id: p.vehiculo_id,
        peso_bruto: String(p.peso_bruto), tara: String(p.tara),
        observaciones: p.observaciones || "", descuentos: descMap,
        equipo_id: p.equipo_id || null,
        origen_peso_bruto: p.origen_peso_bruto || "manual",
        origen_peso_tara: p.origen_peso_tara || "manual",
      });
    } else {
      setPesajeForm({ ...emptyPesaje(), equipo_id: equipos[0]?.id || null });
    }
    setModalPesaje(p || {});
  };

  const calculo = useMemo(() => {
    const bruto = Number(pesajeForm.peso_bruto) || 0;
    const tara = Number(pesajeForm.tara) || 0;
    const pesoNeto = calcularPesoNeto(bruto, tara);
    const porcentajes = Object.values(pesajeForm.descuentos || {}).filter(v => Number(v) > 0);
    const pesoFinal = calcularPesoFinal(pesoNeto, porcentajes);
    return { pesoNeto, pesoFinal };
  }, [pesajeForm.peso_bruto, pesajeForm.tara, pesajeForm.descuentos]);

  const equipoSel = useMemo(() => equipos.find(q => q.id === pesajeForm.equipo_id) || null, [equipos, pesajeForm.equipo_id]);
  const canCapturar = isAdmin || hasPermission("bascula.capturar_peso_equipo");
  // Si la balanza no permite digitar y el usuario puede capturar, el campo queda de solo lectura
  const soloBalanza = !!equipoSel && equipoSel.permite_digitar_peso === false && canCapturar;

  const capturarPeso = async (campo, origenCampo) => {
    if (!equipoSel) return;
    setCapturando(campo);
    try {
      const r = await leerPeso(equipoSel);
      setPesajeForm(f => ({ ...f, [campo]: String(r.kg), [origenCampo]: "balanza" }));
      if (r.simulado) toast.warning(`Peso simulado: ${r.kg} kg (balanza en modo simulación)`);
      else toast.success(`Peso leído: ${r.kg} kg`);
    } catch (e) {
      toast.error(e.message || "No se pudo leer la balanza");
    }
    setCapturando(null);
  };

  const guardarPesaje = async () => {
    const bruto = Number(pesajeForm.peso_bruto);
    const tara = Number(pesajeForm.tara);
    if (!bruto || tara === "" || tara === null || isNaN(tara)) { toast.error("Peso bruto y tara son obligatorios"); return; }
    if (tara > bruto) { toast.error("La tara no puede ser mayor al peso bruto"); return; }
    setSaving(true);
    try {
      const esEdicion = !!modalPesaje?.id;
      const url = esEdicion ? `${API}/admin/bascula/pesajes/${modalPesaje.id}` : `${API}/admin/bascula/pesajes${logoQs}`;
      const descuentos = Object.entries(pesajeForm.descuentos || {})
        .filter(([, pct]) => Number(pct) > 0)
        .map(([tipo_descuento_id, porcentaje]) => ({ tipo_descuento_id, porcentaje: Number(porcentaje) }));
      const payload = { ...pesajeForm, peso_bruto: bruto, tara, descuentos };
      const res = await fetch(url, {
        method: esEdicion ? "PUT" : "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Error al guardar"); }
      toast.success(esEdicion ? "Pesaje actualizado" : "Pesaje registrado");
      setModalPesaje(null);
      fetchAll();
    } catch (e) { toast.error(e.message || "Error al guardar"); }
    setSaving(false);
  };

  const eliminarPesaje = async (p) => {
    if (!window.confirm(`¿Eliminar el pesaje ${p.numero}?`)) return;
    try {
      const res = await fetch(`${API}/admin/bascula/pesajes/${p.id}`, { method: "DELETE", headers });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Error al eliminar"); }
      toast.success("Pesaje eliminado");
      fetchAll();
    } catch (e) { toast.error(e.message); }
  };

  // ── Deep-links: Venta -> Facturas / Compra -> Compras ──────────────────────
  useEffect(() => { setSeleccion(prev => new Set([...prev].filter(id => pesajes.some(p => p.id === id && !p.factura_id && !p.compra_id)))); }, [pesajes]);
  const esFacturable = (p) => p.direccion === "venta" && !p.factura_id && !p.compra_id;
  const toggleSel = (id) => setSeleccion(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const facturarSeleccion = () => {
    const elegidos = pesajes.filter(p => seleccion.has(p.id) && esFacturable(p));
    if (!elegidos.length) { toast.error("Seleccioná al menos una carga de venta sin facturar"); return; }
    if (elegidos.some(p => !p.entidad_id)) { toast.error("Hay pesajes sin cliente asignado"); return; }
    if (new Set(elegidos.map(p => p.entidad_id)).size > 1) { toast.error("Las cargas seleccionadas son de clientes distintos. Una factura es para un solo cliente."); return; }
    const params = new URLSearchParams({ nuevo: "factura_bascula", pesaje_ids: elegidos.map(p => p.id).join(",") });
    navigate(`/sistema/ventas?${params.toString()}`);
  };
  const registrarCompraPesaje = (p) => {
    if (!p.entidad_id) { toast.error("El pesaje no tiene proveedor asignado"); return; }
    const params = new URLSearchParams({ nuevo: "compra_bascula", pesaje_id: p.id });
    navigate(`/sistema/egresos?${params.toString()}`);
  };

  // ── Tipos de descuento ───────────────────────────────────────────────────────
  const openTipoDescuento = (t) => { setTipoDescuentoForm(t ? { nombre: t.nombre, activo: t.activo } : { nombre: "", activo: true }); setModalTipoDescuento(t || {}); };

  const guardarTipoDescuento = async () => {
    if (!tipoDescuentoForm.nombre?.trim()) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    try {
      const esEdicion = !!modalTipoDescuento?.id;
      const url = esEdicion ? `${API}/admin/bascula/tipos-descuento/${modalTipoDescuento.id}` : `${API}/admin/bascula/tipos-descuento${logoQs}`;
      const res = await fetch(url, {
        method: esEdicion ? "PUT" : "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(tipoDescuentoForm),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Error al guardar"); }
      toast.success(esEdicion ? "Tipo de descuento actualizado" : "Tipo de descuento creado");
      setModalTipoDescuento(null);
      fetchAll();
    } catch (e) { toast.error(e.message || "Error al guardar"); }
    setSaving(false);
  };

  const eliminarTipoDescuento = async (t) => {
    if (!window.confirm(`¿Eliminar/desactivar el tipo de descuento "${t.nombre}"?`)) return;
    try {
      const res = await fetch(`${API}/admin/bascula/tipos-descuento/${t.id}`, { method: "DELETE", headers });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Error al eliminar"); }
      const data = await res.json().catch(() => ({}));
      toast.success(data.mensaje || "Tipo de descuento eliminado");
      fetchAll();
    } catch (e) { toast.error(e.message); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-arandu-dark flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-arandu-blue animate-spin" />
      </div>
    );
  }

  if (!canVer) {
    return (
      <div className="min-h-screen bg-arandu-dark flex items-center justify-center text-slate-400">
        No tenés permiso para ver este módulo.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-arandu-dark text-white">
      {/* Header */}
      <div className="border-b border-white/10 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-white p-1 -ml-1 rounded-lg hover:bg-white/5 transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <Scale className="w-6 h-6 text-emerald-400" />
            <div>
              <h1 className="text-lg font-bold text-white">Báscula</h1>
              <p className="text-xs text-slate-400">{activeEmpresaPropia?.nombre || ""} — Registro de pesajes de granos</p>
            </div>
          </div>
          <button onClick={handleRefresh} disabled={refreshing} className={BTN_SEC}>
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 px-6 pt-4 border-b border-white/10">
        {[
          { key: "pesajes", label: "Pesajes", icon: <Scale className="w-4 h-4" /> },
          { key: "descuentos", label: "Tipos de Descuento", icon: <Settings className="w-4 h-4" /> },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors whitespace-nowrap ${
              tab === t.key ? "text-white border-blue-500 bg-blue-500/10" : "text-slate-400 border-transparent hover:text-white hover:border-white/20"
            }`}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="p-6">
        {tab === "pesajes" && (
          <div>
            <div className="flex justify-end items-center gap-2 mb-4">
              {(isAdmin || hasPermission("facturas.crear")) && (
                <button onClick={facturarSeleccion} disabled={seleccion.size === 0}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed">
                  <Receipt className="w-4 h-4" /> Facturar{seleccion.size > 0 ? ` (${seleccion.size})` : ""}
                </button>
              )}
              {canCrear && (
                <button onClick={() => openPesaje(null)} className={BTN_PRI}>
                  <Plus className="w-4 h-4" /> Nuevo Pesaje
                </button>
              )}
            </div>
            <div className="bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-slate-400 text-xs uppercase">
                  <tr>
                    <th className="px-3 py-2 w-8">
                      <input type="checkbox" className="accent-emerald-500"
                        checked={pesajes.filter(esFacturable).length > 0 && pesajes.filter(esFacturable).every(p => seleccion.has(p.id))}
                        onChange={e => setSeleccion(e.target.checked ? new Set(pesajes.filter(esFacturable).map(p => p.id)) : new Set())} />
                    </th>
                    <th className="text-left px-4 py-2">N°</th>
                    <th className="text-left px-4 py-2">Fecha</th>
                    <th className="text-left px-4 py-2">Dirección</th>
                    <th className="text-left px-4 py-2">Entidad</th>
                    <th className="text-left px-4 py-2">Producto</th>
                    <th className="text-left px-4 py-2">Vehículo / Chofer</th>
                    <th className="text-right px-4 py-2">Bruto</th>
                    <th className="text-right px-4 py-2">Tara</th>
                    <th className="text-right px-4 py-2">Neto</th>
                    <th className="text-right px-4 py-2">Comercial Final</th>
                    <th className="text-left px-4 py-2">Comprobante</th>
                    <th className="text-right px-4 py-2">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {pesajes.length === 0 && (
                    <tr><td colSpan={13} className="text-center py-8 text-slate-500">Sin pesajes registrados</td></tr>
                  )}
                  {pesajes.map(p => {
                    const vinculado = p.factura_id || p.compra_id;
                    return (
                      <tr key={p.id} className="hover:bg-white/[0.02]">
                        <td className="px-3 py-2">
                          {esFacturable(p) && <input type="checkbox" className="accent-emerald-500" checked={seleccion.has(p.id)} onChange={() => toggleSel(p.id)} />}
                        </td>
                        <td className="px-4 py-2 font-medium">{p.numero}</td>
                        <td className="px-4 py-2 text-slate-400">{p.fecha}</td>
                        <td className="px-4 py-2">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${DIRECCION_COLOR[p.direccion] || ""}`}>{DIRECCION_LABEL[p.direccion] || p.direccion}</span>
                        </td>
                        <td className="px-4 py-2">{p.entidad_nombre || "—"}</td>
                        <td className="px-4 py-2 text-slate-400">{p.producto_nombre || "—"}</td>
                        <td className="px-4 py-2 text-slate-400">{p.vehiculo_placa || "—"}{p.chofer_nombre ? ` · ${p.chofer_nombre}` : ""}</td>
                        <td className="px-4 py-2 text-right">{fmtKg(p.peso_bruto)}</td>
                        <td className="px-4 py-2 text-right">{fmtKg(p.tara)}</td>
                        <td className="px-4 py-2 text-right text-slate-400">{fmtKg(p.peso_neto)}</td>
                        <td className="px-4 py-2 text-right font-medium">{fmtKg(p.peso_neto_final)}</td>
                        <td className="px-4 py-2">
                          {vinculado ? (
                            <span className="text-[10px] px-1.5 py-0.5 rounded border text-violet-400 bg-violet-400/10 border-violet-400/20 w-fit">
                              {p.factura_numero || p.compra_numero || "Vinculado"}
                            </span>
                          ) : (
                            <span className="text-[10px] px-1.5 py-0.5 rounded border text-slate-400 bg-slate-400/10 border-slate-400/20 w-fit">Sin vincular</span>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex items-center justify-end gap-1">
                            {!vinculado && p.direccion === "compra" && (isAdmin || hasPermission("compras.crear")) && (
                              <button onClick={() => registrarCompraPesaje(p)} className="p-1.5 text-slate-400 hover:text-amber-400 rounded-lg hover:bg-white/10" title="Registrar Compra">
                                <ShoppingCart className="w-4 h-4" />
                              </button>
                            )}
                            <button onClick={() => setPesajeAImprimir(p)} className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-white/10" title="Imprimir comprobante">
                              <Printer className="w-4 h-4" />
                            </button>
                            {canEditar && !vinculado && (
                              <button onClick={() => openPesaje(p)} className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-white/10" title="Editar">
                                <Pencil className="w-4 h-4" />
                              </button>
                            )}
                            {canEliminar && !vinculado && (
                              <button onClick={() => eliminarPesaje(p)} className="p-1.5 text-slate-400 hover:text-red-400 rounded-lg hover:bg-white/10" title="Eliminar">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "descuentos" && (
          <div>
            <p className="text-xs text-slate-500 mb-4">
              Los 9 tipos de descuento por defecto (Humedad, Impurezas, Calcinados, PH, Partidos, Dañado, Verdín, Punta Negra, Brotados) ya vienen cargados.
              Podés agregar otros propios de esta empresa, o desactivar los que no uses.
            </p>
            <div className="flex justify-end mb-4">
              {canConfigurarDescuentos && (
                <button onClick={() => openTipoDescuento(null)} className={BTN_PRI}>
                  <Plus className="w-4 h-4" /> Nuevo Tipo de Descuento
                </button>
              )}
            </div>
            <div className="bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-slate-400 text-xs uppercase">
                  <tr>
                    <th className="text-left px-4 py-2">Nombre</th>
                    <th className="text-left px-4 py-2">Origen</th>
                    <th className="text-left px-4 py-2">Estado</th>
                    <th className="text-right px-4 py-2">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {tiposDescuento.map(t => (
                    <tr key={t.id} className="hover:bg-white/[0.02]">
                      <td className="px-4 py-2 font-medium">{t.nombre}</td>
                      <td className="px-4 py-2 text-slate-400">{t.es_default ? "Default del sistema" : "Agregado por la empresa"}</td>
                      <td className="px-4 py-2">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${t.activo ? "text-emerald-400 bg-emerald-400/10 border-emerald-400/20" : "text-slate-400 bg-slate-400/10 border-slate-400/20"}`}>
                          {t.activo ? "Activo" : "Inactivo"}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center justify-end gap-1">
                          {canConfigurarDescuentos && (
                            <button onClick={() => openTipoDescuento(t)} className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-white/10" title="Editar">
                              <Pencil className="w-4 h-4" />
                            </button>
                          )}
                          {canConfigurarDescuentos && (
                            <button onClick={() => eliminarTipoDescuento(t)} className="p-1.5 text-slate-400 hover:text-red-400 rounded-lg hover:bg-white/10" title="Eliminar/Desactivar">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Modal Nuevo/Editar Pesaje */}
      {modalPesaje && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-arandu-dark-light border border-white/10 rounded-xl w-full max-w-2xl p-6 my-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl font-bold text-white">{modalPesaje?.id ? "Editar Pesaje" : "Nuevo Pesaje"}</h2>
              <button onClick={() => setModalPesaje(null)} className="text-slate-400 hover:text-white"><X className="w-6 h-6" /></button>
            </div>

            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className={LABEL}>Dirección *</label>
                  <select
                    className={INPUT}
                    value={pesajeForm.direccion}
                    onChange={(e) => setPesajeForm(f => ({ ...f, direccion: e.target.value, entidad_id: null }))}
                    style={{ colorScheme: "dark" }}
                  >
                    <option value="compra">Compra (a Proveedor)</option>
                    <option value="venta">Venta (a Cliente)</option>
                  </select>
                </div>
                <div>
                  <label className={LABEL}>Fecha *</label>
                  <DateInput className={INPUT} value={pesajeForm.fecha} onChange={(e) => setPesajeForm(f => ({ ...f, fecha: e.target.value }))} />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className={LABEL}>{pesajeForm.direccion === "compra" ? "Proveedor" : "Cliente"}</label>
                  <EntidadSearchSelect
                    entidades={pesajeForm.direccion === "compra" ? proveedores : clientes}
                    value={pesajeForm.entidad_id}
                    onSelect={(id) => setPesajeForm(f => ({ ...f, entidad_id: id }))}
                    placeholder={pesajeForm.direccion === "compra" ? "Buscar proveedor..." : "Buscar cliente..."}
                  />
                </div>
                <div>
                  <label className={LABEL}>Producto</label>
                  <select
                    className={INPUT}
                    value={pesajeForm.producto_id || ""}
                    onChange={(e) => setPesajeForm(f => ({ ...f, producto_id: e.target.value || null }))}
                    style={{ colorScheme: "dark" }}
                  >
                    <option value="">Sin especificar</option>
                    {productos.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className={LABEL}>Chofer</label>
                  <EntidadSearchSelect entidades={choferes} value={pesajeForm.chofer_id} onSelect={(id) => setPesajeForm(f => ({ ...f, chofer_id: id }))} placeholder="Buscar chofer..." />
                </div>
                <div>
                  <label className={LABEL}>Vehículo</label>
                  <select
                    className={INPUT}
                    value={pesajeForm.vehiculo_id || ""}
                    onChange={(e) => setPesajeForm(f => ({ ...f, vehiculo_id: e.target.value || null }))}
                    style={{ colorScheme: "dark" }}
                  >
                    <option value="">Sin especificar</option>
                    {vehiculos.map(v => <option key={v.id} value={v.id}>{v.placa}</option>)}
                  </select>
                  {vehiculos.length === 0 && (
                    <p className="text-[10px] text-slate-500 mt-1">No hay vehículos cargados — se agregan desde el módulo Transporte.</p>
                  )}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {equipos.length > 0 && (
                  <div className="md:col-span-2">
                    <label className={LABEL}>Balanza</label>
                    <select
                      className={INPUT}
                      value={pesajeForm.equipo_id || ""}
                      onChange={(e) => setPesajeForm(f => ({ ...f, equipo_id: e.target.value || null }))}
                    >
                      <option value="">Sin balanza (peso digitado)</option>
                      {equipos.map(q => (
                        <option key={q.id} value={q.id}>
                          {q.codigo}{q.ubicacion ? ` — ${q.ubicacion}` : ""}{q.modo_simulacion ? " (simulación)" : ""}
                        </option>
                      ))}
                    </select>
                    {equipoSel?.modo_simulacion && (
                      <p className="text-[10px] text-amber-400 mt-1">Esta balanza está en modo simulación: los pesos son de prueba, no de una báscula real.</p>
                    )}
                  </div>
                )}
                {[["peso_bruto", "origen_peso_bruto", "Peso Bruto (kg) *"], ["tara", "origen_peso_tara", "Tara (kg) *"]].map(([campo, origenCampo, etiqueta]) => (
                  <div key={campo}>
                    <label className={LABEL}>{etiqueta}</label>
                    <div className="flex gap-2">
                      <input
                        type="number" step="0.01" className={INPUT}
                        value={pesajeForm[campo]}
                        readOnly={soloBalanza}
                        title={soloBalanza ? "Esta balanza no permite digitar el peso: use Capturar" : undefined}
                        onChange={(e) => setPesajeForm(f => ({ ...f, [campo]: e.target.value, [origenCampo]: "manual" }))}
                      />
                      {equipoSel && canCapturar && (
                        <button type="button" className={BTN_SEC} disabled={capturando === campo} onClick={() => capturarPeso(campo, origenCampo)}>
                          {capturando === campo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Scale className="w-4 h-4" />}
                          Capturar
                        </button>
                      )}
                    </div>
                    {pesajeForm[origenCampo] === "balanza" && (
                      <p className="text-[10px] text-emerald-400 mt-1">Leído de la balanza {equipoSel?.codigo || ""}</p>
                    )}
                  </div>
                ))}
              </div>

              {/* Descuentos por línea */}
              <div>
                <label className={LABEL}>Descuentos de calidad (%)</label>
                <div className="grid md:grid-cols-3 gap-2 bg-white/[0.02] border border-white/10 rounded-lg p-3">
                  {tiposDescuento.filter(t => t.activo).map(t => (
                    <div key={t.id} className="flex items-center gap-2">
                      <span className="text-xs text-slate-400 flex-1 truncate">{t.nombre}</span>
                      <input
                        type="number" step="0.01" min="0" max="100"
                        className="w-20 bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-xs text-white text-right"
                        value={pesajeForm.descuentos[t.id] ?? ""}
                        onChange={(e) => setPesajeForm(f => ({ ...f, descuentos: { ...f.descuentos, [t.id]: e.target.value } }))}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Cálculo en vivo */}
              <div className="grid grid-cols-2 gap-4 bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3">
                <div>
                  <span className="block text-[10px] uppercase text-slate-500">Peso Neto</span>
                  <span className="block text-lg font-bold text-white">{fmtKg(calculo.pesoNeto)}</span>
                </div>
                <div>
                  <span className="block text-[10px] uppercase text-slate-500">Peso Comercial Final</span>
                  <span className="block text-lg font-bold text-emerald-400">{fmtKg(calculo.pesoFinal)}</span>
                </div>
              </div>

              <div>
                <label className={LABEL}>Observaciones</label>
                <textarea className={INPUT} rows={2} value={pesajeForm.observaciones} onChange={(e) => setPesajeForm(f => ({ ...f, observaciones: e.target.value }))} />
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setModalPesaje(null)} className="flex-1 px-4 py-2 text-sm bg-white/5 hover:bg-white/10 text-slate-300 rounded-lg border border-white/10">
                  <X className="w-4 h-4 inline mr-2" /> Cancelar
                </button>
                <button type="button" onClick={guardarPesaje} disabled={saving} className={`flex-1 ${BTN_PRI} justify-center`}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Nuevo/Editar Tipo de Descuento */}
      {modalTipoDescuento && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-arandu-dark-light border border-white/10 rounded-xl w-full max-w-sm p-6 my-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl font-bold text-white">{modalTipoDescuento?.id ? "Editar" : "Nuevo"} Tipo de Descuento</h2>
              <button onClick={() => setModalTipoDescuento(null)} className="text-slate-400 hover:text-white"><X className="w-6 h-6" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className={LABEL}>Nombre *</label>
                <input className={INPUT} value={tipoDescuentoForm.nombre} onChange={(e) => setTipoDescuentoForm(f => ({ ...f, nombre: e.target.value }))} placeholder="Ej: OLOR" />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input type="checkbox" checked={tipoDescuentoForm.activo} onChange={(e) => setTipoDescuentoForm(f => ({ ...f, activo: e.target.checked }))} />
                Activo
              </label>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setModalTipoDescuento(null)} className="flex-1 px-4 py-2 text-sm bg-white/5 hover:bg-white/10 text-slate-300 rounded-lg border border-white/10">
                  Cancelar
                </button>
                <button type="button" onClick={guardarTipoDescuento} disabled={saving} className={`flex-1 ${BTN_PRI} justify-center`}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Guardar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Comprobante imprimible */}
      {pesajeAImprimir && (
        <PesajeDocModal pesaje={pesajeAImprimir} empresa={activeEmpresaPropia} onClose={() => setPesajeAImprimir(null)} />
      )}
    </div>
  );
}

function PesajeDocModal({ pesaje, empresa, onClose }) {
  const handlePrint = () => window.print();
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 overflow-y-auto print:bg-white print:p-0">
      <div className="bg-white text-black rounded-xl w-full max-w-md p-6 my-8 print:my-0 print:rounded-none print:shadow-none" id="pesaje-print-area">
        <div className="flex items-center justify-between mb-4 print:hidden">
          <h2 className="font-bold text-lg">Comprobante de Pesaje</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-black"><X className="w-5 h-5" /></button>
        </div>
        <div className="text-center mb-4">
          <p className="font-bold text-base">{empresa?.nombre_fantasia || empresa?.nombre || ""}</p>
          {empresa?.ruc && <p className="text-xs">RUC: {empresa.ruc}</p>}
          <p className="text-sm font-semibold mt-2">COMPROBANTE DE PESAJE N° {pesaje.numero}</p>
        </div>
        <div className="text-sm space-y-1 border-t border-b border-black/20 py-3 my-3">
          <div className="flex justify-between"><span>Fecha:</span><span>{pesaje.fecha}</span></div>
          <div className="flex justify-between"><span>Dirección:</span><span>{DIRECCION_LABEL[pesaje.direccion] || pesaje.direccion}</span></div>
          <div className="flex justify-between"><span>{pesaje.direccion === "compra" ? "Proveedor" : "Cliente"}:</span><span>{pesaje.entidad_nombre || "—"}</span></div>
          <div className="flex justify-between"><span>Producto:</span><span>{pesaje.producto_nombre || "—"}</span></div>
          <div className="flex justify-between"><span>Chofer:</span><span>{pesaje.chofer_nombre || "—"}</span></div>
          <div className="flex justify-between"><span>Vehículo:</span><span>{pesaje.vehiculo_placa || "—"}</span></div>
        </div>
        <div className="text-sm space-y-1">
          <div className="flex justify-between"><span>Peso Bruto:</span><span>{fmtKg(pesaje.peso_bruto)}</span></div>
          <div className="flex justify-between"><span>Tara:</span><span>{fmtKg(pesaje.tara)}</span></div>
          <div className="flex justify-between font-semibold"><span>Peso Neto:</span><span>{fmtKg(pesaje.peso_neto)}</span></div>
          {(pesaje.descuentos || []).length > 0 && (
            <div className="pt-2 border-t border-black/10 mt-2">
              <p className="text-xs font-semibold mb-1">Descuentos de calidad</p>
              {pesaje.descuentos.map((d, i) => (
                <div key={i} className="flex justify-between text-xs"><span>{d.tipo_descuento_nombre}</span><span>{d.porcentaje}%</span></div>
              ))}
            </div>
          )}
          <div className="flex justify-between font-bold text-base pt-2 border-t border-black/20 mt-2">
            <span>Peso Comercial Final:</span><span>{fmtKg(pesaje.peso_neto_final)}</span>
          </div>
        </div>
        {pesaje.observaciones && <p className="text-xs mt-3 italic">{pesaje.observaciones}</p>}
        <div className="print:hidden pt-4">
          <button onClick={handlePrint} className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg">
            <Printer className="w-4 h-4" /> Imprimir
          </button>
        </div>
      </div>
    </div>
  );
}
